/**
 * Pure-TypeScript forward pass for the exp24 "PitchNet" model (SCORZA-702).
 *
 * The network is a tiny PointNet — three 1x1 convolutions (i.e. per-point linear
 * layers) with ReLU, a global max-pool over the points, then a three-layer MLP
 * head. It's ~21M multiply-adds for a 512-point cloud, a few milliseconds in JS.
 * Reimplementing it directly lets us DROP the onnxruntime-web dependency entirely
 * (~13.5 MB of WASM + its loader) — we only ship the ~325 KB of weights.
 *
 * The ops mirror the ONNX graph exactly (verified against the reference model
 * output to float precision in pitchModelForward.test.ts):
 *   cloud (N,2) -> [Conv1d(2->64) relu] -> [Conv1d(64->128) relu]
 *              -> [Conv1d(128->256) relu] -> ReduceMax over N (256)
 *              -> [Linear(256->128) relu] -> [Linear(128->64) relu]
 *              -> Linear(64->6) -> params(6)
 * Conv weights are stored row-major [out][in]; Linear (Gemm transB=1) likewise.
 */

import {
    PITCHNET_MEAN4,
    PITCHNET_STD4,
    PITCHNET_WEIGHTS_BASE64,
} from "./pitchNetWeights";

interface Layer {
    w: Float32Array; // row-major [out][in]
    b: Float32Array; // [out]
    out: number;
    in: number;
}

export interface PitchNetWeights {
    conv1: Layer;
    conv2: Layer;
    conv3: Layer;
    head0: Layer;
    head3: Layer;
    head5: Layer;
}

export interface PitchNetModel {
    weights: PitchNetWeights;
    mean4: number[];
    std4: number[];
}

function b64ToFloat32(b64: string): Float32Array {
    // atob exists in browsers and Node >= 16 (refsix-core is consumed by
    // both); fall back to Buffer for any non-DOM runtime without it.
    let bytes: Uint8Array;
    if (typeof atob === "function") {
        const bin = atob(b64);
        bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) {
            bytes[i] = bin.charCodeAt(i);
        }
    } else {
        // Copy into a fresh array: a pooled Buffer's byteOffset may not be
        // 4-aligned, which the Float32Array view below would reject.
        bytes = new Uint8Array((globalThis as any).Buffer.from(b64, "base64"));
    }
    if (bytes.length % 4 !== 0) {
        throw new Error(
            `PitchNet weight blob is corrupt: ${bytes.length} bytes is not a whole number of float32s`
        );
    }
    // The blob is little-endian float32; all target platforms are little-endian.
    return new Float32Array(bytes.buffer, bytes.byteOffset, bytes.length / 4);
}

/**
 * Slice the flat weight blob into named layers. The order and shapes are fixed
 * by the exporter (export_pitchnet_weights.py) and the network architecture.
 */
export function decodePitchNetWeights(base64: string): PitchNetWeights {
    const flat = b64ToFloat32(base64);
    let off = 0;
    const take = (n: number): Float32Array => {
        const slice = flat.subarray(off, off + n);
        off += n;
        return slice;
    };
    const layer = (outC: number, inC: number): Layer => ({
        w: take(outC * inC),
        b: take(outC),
        out: outC,
        in: inC,
    });
    const weights: PitchNetWeights = {
        conv1: layer(64, 2),
        conv2: layer(128, 64),
        conv3: layer(256, 128),
        head0: layer(128, 256),
        head3: layer(64, 128),
        head5: layer(6, 64),
    };
    if (off !== flat.length) {
        throw new Error(
            `PitchNet weight blob size mismatch: consumed ${off} of ${flat.length}`
        );
    }
    return weights;
}

/** y[o] = relu?(b[o] + sum_i W[o][i] * x[i]) — one dense/1x1-conv unit. */
function dense(
    layer: Layer,
    x: Float32Array | number[],
    relu: boolean,
    out: Float32Array
): Float32Array {
    const { w, b, out: outC, in: inC } = layer;
    for (let o = 0; o < outC; o++) {
        let s = b[o];
        const base = o * inC;
        for (let i = 0; i < inC; i++) {
            s += w[base + i] * x[i];
        }
        out[o] = relu && s < 0 ? 0 : s;
    }
    return out;
}

/**
 * Run the model on a 512x2 point cloud (flattened row-major [x0,y0,x1,y1,...]).
 * Returns the raw 6-vector output `[cx, cy, log_half_L, log_half_W, sin2t,
 * cos2t]` (first four still standardised — de-standardise with mean4/std4).
 */
export function forwardPitchNet(
    cloud: Float32Array,
    weights: PitchNetWeights
): Float32Array {
    const nPoints = cloud.length / 2;
    const { conv1, conv2, conv3, head0, head3, head5 } = weights;

    const h1 = new Float32Array(conv1.out);
    const h2 = new Float32Array(conv2.out);
    const h3 = new Float32Array(conv3.out);
    const point = new Float32Array(2);

    // Shared per-point MLP, accumulating the global max-pool feature.
    const pooled = new Float32Array(conv3.out).fill(-Infinity);
    for (let p = 0; p < nPoints; p++) {
        point[0] = cloud[p * 2];
        point[1] = cloud[p * 2 + 1];
        dense(conv1, point, true, h1);
        dense(conv2, h1, true, h2);
        dense(conv3, h2, true, h3);
        for (let c = 0; c < h3.length; c++) {
            if (h3[c] > pooled[c]) pooled[c] = h3[c];
        }
    }

    // MLP head.
    const g1 = dense(head0, pooled, true, new Float32Array(head0.out));
    const g2 = dense(head3, g1, true, new Float32Array(head3.out));
    return dense(head5, g2, false, new Float32Array(head5.out));
}

let cachedModel: PitchNetModel | null = null;

/**
 * Get (and memoise) the embedded PitchNet model. The weight blob is a base64
 * string in a sibling module; decoding it into typed arrays is deferred to the
 * first call (i.e. the first time auto-calibration actually runs), not app start.
 */
export function getPitchNetModel(): PitchNetModel {
    if (!cachedModel) {
        cachedModel = {
            weights: decodePitchNetWeights(PITCHNET_WEIGHTS_BASE64),
            mean4: PITCHNET_MEAN4,
            std4: PITCHNET_STD4,
        };
    }
    return cachedModel;
}
